/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/platform-admin/AuditLogViewer.tsx
 * PURPOSE: Audit Log Viewer for Platform Admin - Sprint 28 ADR-039 Track B
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 9: Documentation Standards
 *
 * DATE: 2026-03-28
 * COMPLIANCE: ADR-039 Enterprise Platform Admin RBAC Architecture v3.1.0
 *
 * @business-context
 * - Domain: Platform Administration, Compliance, Security Audit
 * - Impact: Security compliance visibility, audit trail transparency
 * - Stakeholders: SECURITY_ADMIN, COMPLIANCE_ADMIN, PLATFORM_OPERATOR
 *
 * @technical-architecture
 * - Layer: Presentation/UI
 * - Pattern: React functional component with CSS Modules + NQH design tokens
 * - Dependencies: React, platformAdminApi, CSS Modules
 *
 * @cross-references
 * - API: backend/app/api/v1/platform_admin.py (GET /audit-logs, GET /audit-logs/export)
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - Design: docs/02-design/18-adrs/06-decisions/DD-0108-tenant-tier-change-feature-flags-merge-and-audit.md
 * - Sprint: S75-009 (CSS modules migration + Vietnamese labels)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { usePlatformAdminAuth } from '../../contexts/PlatformAdminContext';
import { platformAdminApi, AuditLogEntry, AuditLogFilters, AuditLogListResponse } from '../../services/api/platform-admin/platformAdmin';
import styles from './AuditLogViewer.module.css';

// Event type labels — Vietnamese
const EVENT_TYPE_LABELS: Record<string, string> = {
  'platform_admin.login': 'Đăng nhập',
  'platform_admin.logout': 'Đăng xuất',
  'platform_admin.password_reset': 'Đặt lại mật khẩu',
  'platform_admin.account_locked': 'Khóa tài khoản',
  'platform_admin.account_unlocked': 'Mở khóa tài khoản',
  'platform_admin.ip_whitelist_add': 'Thêm IP Whitelist',
  'platform_admin.ip_whitelist_remove': 'Xóa IP Whitelist',
  'tenant.create': 'Tạo Tenant',
  'tenant.status_change': 'Đổi trạng thái Tenant',
  // BR-TENM-010 / DD-0108 §2.2: PATCH .../tier writes this action string verbatim
  // as PlatformAdminAuditLog.event_type via AuditService.log_platform_admin_action().
  'TENANT_TIER_CHANGE': 'Đổi gói Tenant',
};

// Entity type options — Vietnamese
const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'Tất cả loại thực thể' },
  { value: 'platform_admin', label: 'Quản trị viên' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'ip_whitelist', label: 'IP Whitelist' },
  { value: 'authentication', label: 'Xác thực' },
];

const AuditLogViewer: React.FC = () => {
  const { platformAdmin } = usePlatformAdminAuth();
  const accessToken = platformAdmin.accessToken || '';

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState<AuditLogFilters>({
    skip: 0,
    limit: 50,
  });

  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: AuditLogListResponse = await platformAdminApi.listAuditLogs(accessToken, filters);
      setLogs(response.items);
      setTotal(response.total);
      setHasMore(response.has_more);
    } catch (err: any) {
      setError(err.message || 'Không thể tải nhật ký kiểm toán');
    } finally {
      setLoading(false);
    }
  }, [accessToken, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (key: keyof AuditLogFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      skip: 0,
    }));
  };

  const handleNextPage = () => {
    setFilters(prev => ({
      ...prev,
      skip: (prev.skip || 0) + (prev.limit || 50),
    }));
  };

  const handlePrevPage = () => {
    setFilters(prev => ({
      ...prev,
      skip: Math.max(0, (prev.skip || 0) - (prev.limit || 50)),
    }));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await platformAdminApi.exportAuditLogs(accessToken, {
        event_type: filters.event_type,
        actor_admin_id: filters.actor_admin_id,
        entity_type: filters.entity_type,
        start_date: filters.start_date,
        end_date: filters.end_date,
      });
      platformAdminApi.downloadAuditLogsCSV(blob);
    } catch (err: any) {
      setError(err.message || 'Không thể xuất nhật ký kiểm toán');
    } finally {
      setExporting(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({ skip: 0, limit: 50 });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEventLabel = (eventType: string) => {
    return EVENT_TYPE_LABELS[eventType] || eventType;
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Nhật ký kiểm toán</h1>
          <p className={styles.subtitle}>
            Xem và xuất nhật ký hoạt động quản trị viên để giám sát tuân thủ và bảo mật
          </p>
        </div>
        <button
          className={`${styles.button} ${styles.exportButton}`}
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Đang xuất...' : 'Xuất CSV'}
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className={styles.error}>
          {error}
          <button
            className={styles.errorDismiss}
            onClick={() => setError(null)}
          >
            Bỏ qua
          </button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filtersCard}>
        <div className={styles.filtersGrid}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Loại sự kiện</label>
            <input
              type="text"
              className={styles.filterInput}
              placeholder="VD: platform_admin.password_reset"
              value={filters.event_type || ''}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
            />
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Loại thực thể</label>
            <select
              className={styles.filterSelect}
              value={filters.entity_type || ''}
              onChange={(e) => handleFilterChange('entity_type', e.target.value)}
            >
              {ENTITY_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Mã tương quan</label>
            <input
              type="text"
              className={styles.filterInput}
              placeholder="Tìm theo mã tương quan"
              value={filters.correlation_id || ''}
              onChange={(e) => handleFilterChange('correlation_id', e.target.value)}
            />
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Ngày bắt đầu</label>
            <input
              type="datetime-local"
              className={styles.filterInput}
              value={filters.start_date || ''}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Ngày kết thúc</label>
            <input
              type="datetime-local"
              className={styles.filterInput}
              value={filters.end_date || ''}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
          </div>
        </div>

        <div className={styles.buttonRow}>
          <button
            className={`${styles.button} ${styles.secondaryButton}`}
            onClick={handleClearFilters}
          >
            Xóa bộ lọc
          </button>
          <button
            className={`${styles.button} ${styles.primaryButton}`}
            onClick={fetchLogs}
          >
            Áp dụng bộ lọc
          </button>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Đang tải nhật ký kiểm toán...</div>
        ) : logs.length === 0 ? (
          <div className={styles.loading}>Không tìm thấy nhật ký phù hợp với bộ lọc</div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Thời gian</th>
                  <th className={styles.th}>Sự kiện</th>
                  <th className={styles.th}>Người thực hiện</th>
                  <th className={styles.th}>Thực thể</th>
                  <th className={styles.th}>Mã tương quan</th>
                  <th className={styles.th}>Địa chỉ IP</th>
                  <th className={styles.th}>Chi tiết</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className={styles.td}>{formatDate(log.created_at)}</td>
                    <td className={styles.td}>
                      <span className={`${styles.badge} ${styles.eventBadge}`}>
                        {getEventLabel(log.event_type)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      {log.actor_email || log.actor_admin_id.substring(0, 8) + '...'}
                    </td>
                    <td className={styles.td}>
                      <span className={`${styles.badge} ${styles.entityBadge}`}>
                        {log.entity_type}
                      </span>
                      <div className={styles.entityIdSub}>
                        {log.entity_id.substring(0, 8)}...
                      </div>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.correlationId} title={log.correlation_id}>
                        {log.correlation_id.substring(0, 8)}...
                      </span>
                    </td>
                    <td className={styles.td}>{log.ip_address || '-'}</td>
                    <td className={styles.td}>
                      <span
                        className={styles.detailsCell}
                        onClick={() => setSelectedLog(log)}
                        title="Nhấn để xem chi tiết"
                      >
                        {log.details ? JSON.stringify(log.details).substring(0, 30) + '...' : '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className={styles.pagination}>
              <span className={styles.paginationInfo}>
                Hiển thị {(filters.skip || 0) + 1} - {Math.min((filters.skip || 0) + logs.length, total)} trong tổng số {total} bản ghi
              </span>
              <div className={styles.paginationButtons}>
                <button
                  className={`${styles.button} ${styles.secondaryButton}`}
                  onClick={handlePrevPage}
                  disabled={(filters.skip || 0) === 0}
                >
                  Trước
                </button>
                <button
                  className={`${styles.button} ${styles.secondaryButton}`}
                  onClick={handleNextPage}
                  disabled={!hasMore}
                >
                  Sau
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Modal */}
      {selectedLog && (
        <div className={styles.modal} onClick={() => setSelectedLog(null)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Chi tiết nhật ký kiểm toán</h3>

            <div className={styles.modalDetailRow}>
              <strong>Loại sự kiện:</strong> {selectedLog.event_type}
            </div>
            <div className={styles.modalDetailRow}>
              <strong>Mã tương quan:</strong>
              <span className={styles.correlationId}>
                {selectedLog.correlation_id}
              </span>
            </div>
            <div className={styles.modalDetailRow}>
              <strong>Người thực hiện:</strong> {selectedLog.actor_email || selectedLog.actor_admin_id}
              <div className={styles.modalDetailMeta}>
                Vai trò: {selectedLog.actor_roles.join(', ') || 'N/A'}
              </div>
            </div>
            <div className={styles.modalDetailRow}>
              <strong>Thời gian:</strong> {formatDate(selectedLog.created_at)}
            </div>

            {selectedLog.before_state && (
              <div className={styles.modalDetailRow}>
                <strong>Trạng thái trước:</strong>
                <pre className={styles.codeBlock}>
                  {JSON.stringify(selectedLog.before_state, null, 2)}
                </pre>
              </div>
            )}

            {selectedLog.after_state && (
              <div className={styles.modalDetailRow}>
                <strong>Trạng thái sau:</strong>
                <pre className={styles.codeBlock}>
                  {JSON.stringify(selectedLog.after_state, null, 2)}
                </pre>
              </div>
            )}

            {selectedLog.details && (
              <div className={styles.modalDetailRow}>
                <strong>Chi tiết:</strong>
                <pre className={styles.codeBlock}>
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </div>
            )}

            <div className={styles.buttonRow}>
              <button
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={() => setSelectedLog(null)}
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogViewer;
